/**
 * The downloadable report — one document, used for both the HTML file and the
 * PDF.
 *
 * Deliberately a pure function of the data: no React, no DOM, no clock. The
 * caller passes `generatedAt`, so the same inputs always produce the same
 * bytes and the whole thing is testable.
 *
 * Two rules shaped it:
 *
 * 1. A report is not a screenshot of the page. The page shows the top 8 of a
 *    ranked list and 50 rows at a time because a screen is small; a file has no
 *    such excuse, so everything is complete here. Where the page had to cap
 *    something, this does not.
 * 2. It has to say what it is a report OF. A reader opening this file a month
 *    later cannot see which filters were active, so the filters are printed on
 *    the cover with the period and the row counts.
 *
 * Charts are inline SVG rather than images: self-contained, and vector when
 * printed. Nothing is loaded from the network, so the file works offline and
 * carries no tracking.
 */

const ESCAPES = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

/**
 * Everything written into this document comes from the API — addresses, plates,
 * surveyor comments, warning messages. Escaping is not cosmetic: an unescaped
 * angle bracket in a comment would be markup in a file someone opens later.
 */
export const escapeHtml = (value) =>
  value === null || value === undefined
    ? ""
    : String(value).replace(/[&<>"']/g, (char) => ESCAPES[char]);

const pct = (value) =>
  value === null || value === undefined ? "—" : `${Math.round(value * 100)}%`;

const num = (value) =>
  typeof value === "number" && Number.isFinite(value)
    ? value.toLocaleString("en-GB")
    : "—";

/** Seconds as text, matching what the page shows. */
export const duration = (seconds) => {
  if (!Number.isFinite(seconds) || seconds <= 0) return "—";
  if (seconds < 90) return `${Math.round(seconds)}s`;
  const minutes = seconds / 60;
  if (minutes < 90) return `${minutes.toFixed(minutes < 10 ? 1 : 0)}m`;
  return `${(minutes / 60).toFixed(1)}h`;
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** No Date parsing of API strings: "2026-08-31 09:14:48" is not portable. */
export const stamp = (value) => {
  if (typeof value !== "string" || value.length < 10) return "—";
  // Some backends send "YYYY-MM-DDTHH:MM:SS" instead of the space-separated
  // shape this was written for — normalize before splitting, same fix as
  // peopleList.js's formatModified, or the day and time both silently drop.
  const [date, time = ""] = value.replace("T", " ").split(" ");
  const [year, month, day] = date.split(/[-/]/);
  const name = MONTHS[Number(month) - 1];
  if (!name) return value;
  return `${Number(day)} ${name} ${year}${time ? ` ${time.slice(0, 5)}` : ""}`;
};

/** "2026-08-10" -> "10 Aug 2026" */
export const dateOnly = (iso) => stamp(`${iso} `).trim();

/**
 * What the reader filtered to, in words.
 *
 * Returns [] when nothing is set, so the cover can say "every inspection in the
 * period" rather than printing an empty list.
 */
export const describeFilters = (filters, facets) => {
  if (!filters) return [];
  const out = [];
  const label = (list, key, fallback) =>
    (list || []).find((entry) => entry.key === key || entry.code === key)?.label ||
    fallback;

  if (filters.query) out.push(`Search: "${filters.query}"`);
  if (filters.day) out.push(`Day: ${filters.day}`);
  if (filters.country && filters.country !== "all") {
    out.push(`Country: ${label(facets?.countries, filters.country, filters.country)}`);
  }
  if (filters.result && filters.result !== "all") {
    out.push(`Result: ${filters.result}`);
  }
  if (filters.reviewState && filters.reviewState !== "all") {
    out.push(
      `Review state: ${label(facets?.reviewStates, filters.reviewState, filters.reviewState)}`,
    );
  }
  if (filters.warning && filters.warning !== "all") {
    out.push(
      filters.warning === "any"
        ? "Warnings: any"
        : filters.warning === "none"
          ? "Warnings: none"
          : `Warning: ${label(facets?.warnings, filters.warning, filters.warning)}`,
    );
  }
  if (filters.rating && filters.rating !== "all") {
    out.push(
      filters.rating === "rated"
        ? "Rating: rated only"
        : filters.rating === "unrated"
          ? "Rating: not rated"
          : `Rating: ${filters.rating} out of 5`,
    );
  }
  if (filters.comment && filters.comment !== "all") {
    out.push(
      filters.comment === "with" ? "With a surveyor comment" : "Without a comment",
    );
  }
  if (filters.part && filters.part !== "all") {
    out.push(`Damaged panel: ${label(facets?.parts, filters.part, filters.part)}`);
  }
  return out;
};

const bars = (rows, { color = "#2a78d6", max } = {}) => {
  if (!rows.length) return "";
  const top = max || Math.max(...rows.map((row) => row.count), 1);
  return `<table class="bars">${rows
    .map(
      (row) => `<tr>
        <th scope="row">${escapeHtml(row.label)}</th>
        <td class="bar"><span style="width:${Math.max(
          1,
          (row.count / top) * 100,
        )}%;background:${row.color || color}"></span></td>
        <td class="n">${num(row.count)}</td>
      </tr>`,
    )
    .join("")}</table>`;
};

/** The daily volume, as the same shape the page draws. */
const dailySvg = (daily) => {
  if (!daily?.length) return "";
  const width = 760;
  const height = 120;
  const max = Math.max(...daily.map((day) => day.count), 1);
  const slot = width / daily.length;
  const barWidth = Math.max(2, Math.min(28, slot - 3));
  const columns = daily
    .map((day, index) => {
      if (!day.count) return "";
      const barHeight = Math.max(3, (day.count / max) * (height - 18));
      const x = index * slot + (slot - barWidth) / 2;
      return `<rect x="${x.toFixed(1)}" y="${(height - 18 - barHeight).toFixed(
        1,
      )}" width="${barWidth.toFixed(1)}" height="${barHeight.toFixed(
        1,
      )}" rx="2" fill="#2a78d6"></rect>`;
    })
    .join("");
  return `<svg viewBox="0 0 ${width} ${height}" width="100%" height="${height}" role="img"
      aria-label="Inspections per day">
      ${columns}
      <line x1="0" y1="${height - 18}" x2="${width}" y2="${height - 18}" stroke="#d8dce2"></line>
      <text x="0" y="${height - 5}" class="axis">${escapeHtml(daily[0].date)}</text>
      <text x="${width}" y="${height - 5}" text-anchor="end" class="axis">${escapeHtml(
        daily[daily.length - 1].date,
      )}</text>
    </svg>`;
};

const section = (title, body, note) =>
  body
    ? `<section><h2>${escapeHtml(title)}</h2>${
        note ? `<p class="note">${escapeHtml(note)}</p>` : ""
      }${body}</section>`
    : "";

const STYLES = `
  :root { color-scheme: light; }
  * { box-sizing: border-box; }
  body { margin: 0; padding: 28px 30px 40px; background: #fff; color: #24272b;
         font: 13px/1.5 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
  h1 { margin: 0 0 2px; font-size: 21px; letter-spacing: -0.01em; }
  h2 { margin: 26px 0 9px; font-size: 14px; }
  .sub { color: #5c6268; font-size: 12.5px; margin: 0; }
  .meta { margin: 14px 0 0; border-collapse: collapse; font-size: 12px; }
  .meta th { text-align: left; padding: 2px 14px 2px 0; color: #7c8189; font-weight: 600; white-space: nowrap; }
  .meta td { padding: 2px 0; }
  .tiles { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 16px; }
  .tile { flex: 1 1 150px; border: 1px solid #e9e7e2; border-radius: 9px; padding: 10px 12px; }
  .tile .k { font-size: 9.5px; text-transform: uppercase; letter-spacing: 0.06em; color: #8b9098; font-weight: 700; }
  .tile .v { font-size: 19px; font-weight: 700; margin-top: 3px; }
  .tile .s { font-size: 11px; color: #5c6268; margin-top: 2px; }
  .callout { margin-top: 14px; border: 1px solid #cfe0f7; background: #f2f7fd;
             border-radius: 8px; padding: 9px 11px; font-size: 12px; color: #444a52; }
  .note { color: #5c6268; font-size: 11.5px; margin: 0 0 8px; }
  table.bars { width: 100%; border-collapse: collapse; }
  table.bars th { text-align: left; font-weight: 500; padding: 3px 10px 3px 0; width: 46%; }
  table.bars td.bar { width: 40%; }
  table.bars td.bar span { display: block; height: 8px; border-radius: 4px; }
  table.bars td.bar { background: linear-gradient(#f1f0ed, #f1f0ed) no-repeat; }
  table.bars td.n { text-align: right; font-variant-numeric: tabular-nums; font-weight: 700; width: 52px; }
  table.rows { width: 100%; border-collapse: collapse; margin-top: 6px; font-size: 11px; }
  table.rows th { text-align: left; text-transform: uppercase; letter-spacing: 0.05em;
                  font-size: 9px; color: #8b9098; border-bottom: 1px solid #e9e7e2; padding: 5px 6px; }
  table.rows td { border-bottom: 1px solid #f3f1ed; padding: 5px 6px; vertical-align: top; }
  table.rows td.n { text-align: right; font-variant-numeric: tabular-nums; }
  .pill { display: inline-block; border-radius: 99px; padding: 1px 7px; font-size: 10px; font-weight: 700; }
  .pass { background: #e3f4e8; color: #187a3c; }
  .fail { background: #fbe9e9; color: #c22f2f; }
  .axis { font-size: 10px; fill: #7c8189; }
  footer { margin-top: 26px; border-top: 1px solid #e9e7e2; padding-top: 9px;
           color: #7c8189; font-size: 10.5px; }
  /* Print: repeat the table head on every page and never split a row. */
  @page { size: A4 landscape; margin: 12mm; }
  @media print {
    body { padding: 0; }
    thead { display: table-header-group; }
    tr, section, .tile { page-break-inside: avoid; }
    h2 { page-break-after: avoid; }
  }
`;

/**
 * The report title, which is also the filename the browser's print dialog
 * suggests — so both downloads end up named the same way.
 */
export const reportTitle = (clientLabel, line, range) =>
  `${clientLabel} – ${line === "property" ? "Property" : "Inspection"} Report (${
    range?.from || "?"
  } to ${range?.to || "?"})`;

export const reportFileName = (clientLabel, range, extension) => {
  const slug = String(clientLabel || "report")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
  return `${slug}_report_${range?.from || "start"}_${range?.to || "end"}.${extension}`;
};

export const buildReportHtml = ({
  clientLabel = "Ergo",
  line = "motor",
  range,
  data,
  inspections = [],
  facets,
  filters,
  identityLabel = "Plate",
  reviewLabelFor = (state) => state,
  generatedAt = "",
} = {}) => {
  if (!data) return "";

  const title = reportTitle(clientLabel, line, range);
  const active = describeFilters(filters, facets);

  const tile = (k, v, s) =>
    `<div class="tile"><div class="k">${escapeHtml(k)}</div><div class="v">${v}</div>${
      s ? `<div class="s">${escapeHtml(s)}</div>` : ""
    }</div>`;

  const rowsTable = `<table class="rows">
    <thead><tr>
      <th>Inspection ID</th><th>${escapeHtml(identityLabel)}</th><th>Captured</th>
      <th>Result</th><th>Review state</th><th>Country</th>
      <th class="n">Photos</th><th class="n">Time</th>
      <th>Warnings</th>${line === "property" ? "" : "<th>Damaged parts</th>"}
      <th class="n">Rating</th><th>Comment</th>
    </tr></thead>
    <tbody>${inspections
      .map(
        (row) => `<tr>
        <td>${escapeHtml(row.refNum)}${row.isTest ? " (test)" : ""}</td>
        <td>${escapeHtml(row.identity || "—")}</td>
        <td>${escapeHtml(stamp(row.createdAt))}</td>
        <td>${
          row.result
            ? `<span class="pill ${row.result === "failed" ? "fail" : "pass"}">${escapeHtml(
                row.result.charAt(0).toUpperCase() + row.result.slice(1),
              )}</span>`
            : "—"
        }</td>
        <td>${escapeHtml(row.reviewState ? reviewLabelFor(row.reviewState) : "—")}</td>
        <td>${escapeHtml(row.country?.label || "—")}</td>
        <td class="n">${row.photos || "—"}</td>
        <td class="n">${escapeHtml(duration(row.duration))}</td>
        <td>${
          row.warnings.length
            ? escapeHtml(row.warnings.map((w) => w.message).join("; "))
            : "—"
        }</td>
        ${
          line === "property"
            ? ""
            : `<td>${
                row.parts.length
                  ? escapeHtml(
                      row.parts
                        .map(
                          (part) =>
                            `${part.label}${
                              part.typeLabels.length
                                ? ` (${part.typeLabels.join(", ")})`
                                : ""
                            }`,
                        )
                        .join("; "),
                    )
                  : "—"
              }</td>`
        }
        <td class="n">${row.rating ? row.rating.toFixed(1) : "—"}</td>
        <td>${escapeHtml(row.comment || "—")}</td>
      </tr>`,
      )
      .join("")}</tbody></table>`;

  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(title)}</title>
<style>${STYLES}</style>
</head><body>
<h1>${escapeHtml(title)}</h1>
<p class="sub">${escapeHtml(
    line === "property" ? "Property inspections" : "Motor inspections",
  )} · ${escapeHtml(clientLabel)}</p>

<table class="meta">
  <tr><th>Period</th><td>${escapeHtml(dateOnly(range?.from || ""))} to ${escapeHtml(
    dateOnly(range?.to || ""),
  )}</td></tr>
  <tr><th>Inspections in period</th><td>${num(data.totals.inspections)}</td></tr>
  <tr><th>Rows in this report</th><td>${num(inspections.length)}${
    facets && inspections.length !== facets.total
      ? ` of ${num(facets.total)} read (filtered)`
      : ""
  }</td></tr>
  <tr><th>Filters</th><td>${
    active.length
      ? active.map((entry) => escapeHtml(entry)).join(" · ")
      : "none — every inspection in the period"
  }</td></tr>
  ${generatedAt ? `<tr><th>Generated</th><td>${escapeHtml(generatedAt)}</td></tr>` : ""}
</table>

<div class="tiles">
  ${tile(
    "Inspections",
    num(data.totals.inspections),
    `${num(data.totals.passed)} passed · ${data.totals.failed} failed`,
  )}
  ${tile(
    "Pass rate",
    pct(data.totals.passRate),
    data.totals.failed ? `${data.totals.failed} rejected` : "nothing rejected",
  )}
  ${tile(
    "Median inspection",
    escapeHtml(duration(data.duration.median)),
    data.duration.recorded
      ? `timed on ${data.duration.recorded} of ${data.duration.of} read`
      : "no durations recorded",
  )}
  ${tile(
    "Links completed",
    pct(data.links.completionRate),
    `${data.links.completed} of ${data.links.generated} generated`,
  )}
  ${tile(
    "Surveyor rating",
    data.rating.recorded ? data.rating.mean.toFixed(1) : "—",
    data.rating.recorded
      ? `given on ${data.rating.recorded} of ${data.rating.of} read`
      : "nobody rated an inspection",
  )}
</div>

${
  data.coverage.complete
    ? ""
    : `<p class="callout">The figures above cover the whole period. The breakdowns
       below are worked out from the ${num(data.coverage.rows)} inspections read of
       ${num(data.coverage.total)}, so treat them as a sample rather than a census.</p>`
}

${section(
  "Inspections per day",
  dailySvg(data.daily),
  `${data.daysWithActivity} of ${data.daily.length} days had inspections`,
)}

${section(
  "Capture links",
  bars(
    [
      { label: "Completed", count: data.links.completed },
      { label: "Started, not finished", count: data.links.startedNotCompleted },
      { label: "Never opened", count: data.links.notStarted },
      // The one row that is a problem, marked as the dashboard marks it.
      { label: "Expired unused", count: data.links.expired, color: "#d03b3b" },
    ],
    { max: data.links.generated || 1 },
  ),
  data.links.expired > data.links.completed
    ? "More links expired than were used — a delivery problem rather than an inspection one."
    : null,
)}

${section(
  "What went wrong during capture",
  bars(data.warnings, { color: "#a37f12" }),
  facets
    ? `${facets.withWarning} of ${facets.total} inspections read raised at least one warning. One inspection can appear on several lines.`
    : null,
)}

${section(
  "Why inspections failed",
  bars(
    data.failureReasons.map((entry) => ({ label: entry.reason, count: entry.count })),
    { color: "#d03b3b" },
  ),
)}

${section(
  "Most damaged panels",
  bars(
    data.panels.map((panel) => ({ label: panel.label, count: panel.count })),
    { color: "#c0399a" },
  ),
)}

${section(
  "Damage types",
  bars(
    data.damageTypes.map((type) => ({ label: type.label, count: type.count })),
    { color: "#c0399a" },
  ),
)}

${section(
  "How surveyors rated the inspections",
  data.rating.recorded
    ? bars(
        data.rating.distribution.map((step) => ({
          label: `${step.score} out of 5`,
          count: step.count,
        })),
        { color: "#a37f12" },
      )
    : "",
  data.rating.recorded && data.rating.recorded < 5
    ? `Only ${data.rating.recorded} inspection${
        data.rating.recorded === 1 ? " was" : "s were"
      } rated, so this is too little to read as a trend.`
    : null,
)}

${section(
  `Inspections (${num(inspections.length)})`,
  inspections.length ? rowsTable : "<p class='note'>No inspections matched.</p>",
)}

<footer>${escapeHtml(title)}${
    generatedAt ? ` · generated ${escapeHtml(generatedAt)}` : ""
  } · every ranked list above is complete; the dashboard shows only the top few.</footer>
</body></html>`;
};

export default buildReportHtml;
